import type { BattleLogEvent, SkillName } from "../types";

function normalizeBarSkill(raw: string): SkillName {
  return (raw === "BASIC" ? "SKILL_1" : raw) as SkillName;
}

/** Unique skills from rotation order (first occurrence), capped for battle skill bar. */
export function uniqueRotationSkills(
  rotation: SkillName[] | undefined,
  max = 6,
): SkillName[] {
  if (!rotation?.length) return [];
  const seen = new Set<string>();
  const out: SkillName[] = [];
  for (const raw of rotation) {
    const skill = normalizeBarSkill(raw);
    if (seen.has(skill)) continue;
    seen.add(skill);
    out.push(skill);
    if (out.length >= max) break;
  }
  return out;
}

/** Merge rotation sources; first source wins order, later sources append new skills. */
export function mergeRotationSources(
  sources: Array<SkillName[] | undefined>,
  max = 6,
): SkillName[] {
  const seen = new Set<string>();
  const out: SkillName[] = [];
  for (const rotation of sources) {
    if (!rotation?.length) continue;
    for (const raw of rotation) {
      const skill = normalizeBarSkill(raw);
      if (seen.has(skill)) continue;
      seen.add(skill);
      out.push(skill);
      if (out.length >= max) return out;
    }
  }
  return out;
}

/** Unique player skills in log order (full log — shows entire fight rotation upfront). */
export function playerSkillsFromBattleLog(
  log: BattleLogEvent[],
  viewerPlayerUid?: string,
  max = 6,
): SkillName[] {
  const seen = new Set<string>();
  const out: SkillName[] = [];
  for (const ev of log) {
    if (!ev.skill) continue;
    const isPlayer =
      ev.actor.startsWith("player") || (viewerPlayerUid != null && ev.actor === viewerPlayerUid);
    if (!isPlayer) continue;
    const skill = normalizeBarSkill(ev.skill);
    if (seen.has(skill)) continue;
    seen.add(skill);
    out.push(skill);
    if (out.length >= max) return out;
  }
  return out;
}

/** Bar icons: hero rotation first, then any skills from the complete battle log. */
export function battleBarSkillSeed(
  rotation: SkillName[] | undefined,
  fullLog: BattleLogEvent[] | undefined,
  viewerPlayerUid?: string,
  max = 6,
): SkillName[] {
  return mergeRotationSources(
    [uniqueRotationSkills(rotation, max), playerSkillsFromBattleLog(fullLog ?? [], viewerPlayerUid, max)],
    max,
  );
}

/** Grow frozen list when rotation/log seed arrives late; never remove skills mid-fight. */
export function growFrozenBattleSkills(frozen: SkillName[], seed: SkillName[], max = 6): SkillName[] {
  if (!seed.length) return frozen;
  if (!frozen.length) return seed.slice(0, max);
  if (seed.length > frozen.length && frozen.every((s) => seed.includes(s))) {
    return seed.slice(0, max);
  }
  return mergeRotationSources([frozen, seed], max);
}

/** Keep the skill bar list fixed for the whole fight (survives hero refresh mid-replay). */
export function mergeFrozenBattleSkills(
  frozen: SkillName[],
  seed: SkillName[],
): SkillName[] {
  if (frozen.length > 0) return frozen;
  return seed;
}

/** Decrement all positive cooldown counters by one (one player turn tick). */
export function tickSkillCooldowns(cds: Record<string, number>): Record<string, number> {
  const next: Record<string, number> = { ...cds };
  for (const key of Object.keys(next)) {
    if (next[key] > 0) next[key] -= 1;
    if (next[key] <= 0) delete next[key];
  }
  return next;
}
